import { FilmStrip, Play, Sparkle } from '@phosphor-icons/react';
import type { CompetitorPostDto } from '@reelplicate/api-client/model';
import { Button } from '@reelplicate/ui';

import {
  formatLift,
  platformName,
  postedAgo,
  views,
} from '@/features/competitors/format';

/**
 * Figma "Home / Competitor hook" (133:43): an over-performing competitor
 * post - 48×84 thumbnail with a play glyph, what it says and where it ran,
 * views against their usual, and one-click "Make my version".
 */
export function CompetitorHookRow({
  post,
  making,
  onMakeMyVersion,
  now,
}: {
  post: CompetitorPostDto;
  /** A flow is being made from this post. */
  making: boolean;
  onMakeMyVersion: () => void;
  /** Epoch ms, for "3d ago". */
  now: number;
}) {
  const meta = [
    `@${post.authorHandle}`,
    platformName(post.platform),
    postedAgo(post.postedAt, now),
  ]
    .filter(Boolean)
    .join(' · ');
  const line = post.caption?.split('\n')[0]?.trim();
  return (
    <li className="border-border-subtle flex items-center gap-4 border-b px-1 py-3.5 last:border-b-0">
      <div className="bg-bg-subtle relative flex h-21 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md">
        {post.thumbnailUrl ? (
          <>
            <img
              src={post.thumbnailUrl}
              alt=""
              className="absolute inset-0 size-full object-cover"
            />
            <span aria-hidden className="absolute inset-0 bg-black/12" />
            <Play
              aria-hidden
              weight="fill"
              className="text-icon-inverse relative size-4"
            />
          </>
        ) : (
          <FilmStrip aria-hidden className="text-icon-tertiary size-5" />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="text-body-md-strong text-text-primary line-clamp-2">
          {line ? `“${line}”` : `@${post.authorHandle}’s post`}
        </p>
        <p className="text-caption text-text-secondary">{meta}</p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-0.5">
        <p className="text-body-md-strong text-text-primary">
          {views(post.views)}
          <span className="sr-only"> views</span>
        </p>
        {post.lift !== null && (
          <p className="text-caption text-status-success-fg">
            {formatLift(post.lift)} their usual
          </p>
        )}
      </div>
      <Button
        size="sm"
        variant="outline"
        leadingIcon={Sparkle}
        loading={making}
        onClick={onMakeMyVersion}
      >
        Make my version
      </Button>
    </li>
  );
}
